import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguageContext } from '../../contexts/LanguageContext';
import { cn } from '../../utils/cn';
import { settingsApi } from '../../services/api';

const Header: React.FC = () => {
  const { language, toggleLanguage, t } = useLanguageContext();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [siteSettings, setSiteSettings] = useState<Record<string, string>>({});

  useEffect(() => {
    settingsApi.getWebsiteSettings()
      .then(res => {
        if (res.success && res.data) {
          setSiteSettings(res.data);
        }
      })
      .catch(() => {
        // 获取失败时使用默认值，静默处理
      });
  }, []);

  const siteName = language === 'zh'
    ? (siteSettings.company_name_zh || '惠亚建筑')
    : (siteSettings.company_name_vi || 'Hueya');

  const siteSubtitle = language === 'zh' ? '建筑工程' : 'Construction';
  const logoUrl = siteSettings.site_logo_url || '';

  const navItems = [
    { path: '/', label: t('home') },
    { path: '/about', label: t('about') },
    { path: '/services', label: t('services') },
    { path: '/projects', label: t('projects') },
    { path: '/contact', label: t('contact') },
  ];

  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-sm border-b border-gray-100">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={siteName}
                className="w-10 h-10 object-contain rounded-lg"
                onError={(e) => {
                  // 图片加载失败时显示默认 H 字母
                  const parent = (e.target as HTMLImageElement).parentElement;
                  if (parent) {
                    (e.target as HTMLImageElement).style.display = 'none';
                    const fallback = document.createElement('div');
                    fallback.className = 'w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-800 rounded-lg flex items-center justify-center';
                    fallback.innerHTML = '<span class="text-white font-bold text-xl">H</span>';
                    parent.insertBefore(fallback, parent.firstChild);
                  }
                }}
              />
            ) : (
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-800 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xl">H</span>
              </div>
            )}
            <div>
              <div className="font-bold text-gray-900 text-lg">{siteName}</div>
              <div className="text-xs text-gray-500">{siteSubtitle}</div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className="text-gray-700 hover:text-blue-600 font-medium transition-colors relative group"
              >
                {item.label}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-blue-600 group-hover:w-full transition-all duration-300"></span>
              </Link>
            ))}
          </nav>

          {/* Right Section */}
          <div className="flex items-center space-x-4">
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="px-4 py-2 bg-blue-50 text-blue-700 rounded-lg font-medium hover:bg-blue-100 transition-colors"
            >
              {language === 'zh' ? 'VI' : '中文'}
            </button>

            {/* Contact Button */}
            <Link
              to="/contact"
              className="hidden md:inline-block px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              {t('contactUs')}
            </Link>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100"
            >
              <div className="w-6 h-6 flex flex-col justify-center space-y-1">
                <span className={cn('w-6 h-0.5 bg-gray-700 transition-all', isMenuOpen && 'rotate-45 translate-y-2')}></span>
                <span className={cn('w-6 h-0.5 bg-gray-700 transition-all', isMenuOpen && 'opacity-0')}></span>
                <span className={cn('w-6 h-0.5 bg-gray-700 transition-all', isMenuOpen && '-rotate-45 -translate-y-2')}></span>
              </div>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden border-t border-gray-100 py-4">
            <div className="flex flex-col space-y-3">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMenuOpen(false)}
                  className="py-2 px-4 text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-lg font-medium transition-colors"
                >
                  {item.label}
                </Link>
              ))}
              <Link
                to="/contact"
                onClick={() => setIsMenuOpen(false)}
                className="py-2 px-4 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors text-center"
              >
                {t('contactUs')}
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
